import { IData } from '../../../../Assembler/dist/registry/iface/iData.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { CounterManager } from '../dataReloadable/counterManager.js';
import { CounterFilter } from '../../registryCommon/dynamicData/counterFilter.js';

/**
 * Projects CounterAction transient state into component JSON.
 *
 * The key read, the persistence opt-in and the transient key all live in CounterFilter — the shared pure
 * producer — so this provider no longer depends on the action handler to reuse them and cannot drift from
 * CounterClientData or the client processor.
 */
export class CounterData extends IData {
    private _counterManager: CounterManager | null = null;

    /**
     * Wires the App_Data-backed counter store used for cross-request persistence.
     */
    initialize(counterManager: CounterManager): void {
        this._counterManager = counterManager;
    }

    get typeName(): string {
        return 'CounterData';
    }

    getData(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject {
        const retDataJson = new JsonObject();

        try {
            const counterKey = CounterFilter.readCounterKey(paramJson);

            // Resolution order mirrors CounterAction exactly: this merge's action result first, then the
            // opt-in App_Data-backed store (so a plain reload still shows the persisted value), then the
            // component's own base value. Persist off => the last two collapse to transient behaviour.
            //
            // The resolved value is passed through with its ORIGINAL JSON type rather than coerced to int:
            // the component's own base value is whatever the component JSON declared, and re-typing it here
            // would change the rendered bytes for any binding that spells its counter another way.
            const persist = CounterFilter.readPersistFlag(paramJson);
            const storedCounter = this._counterManager !== null
                ? this._counterManager.tryGetValue(
                    CounterManager.buildCounterKey(appSite, componentName, counterKey)
                )
                : { found: false, value: 0 };
            const transientCounterKey = CounterFilter.buildTransientCounterKey(componentName, counterKey);
            if (transientStore.containsKey(transientCounterKey) === true) {
                const transientValue = transientStore.getValue(transientCounterKey);
                if (transientValue !== null) {
                    retDataJson.set(counterKey, transientValue);
                }
            } else if (persist === true && storedCounter.found === true) {
                retDataJson.set(counterKey, storedCounter.value);
            } else if (componentJson.containsKey(counterKey) === true) {
                retDataJson.set(counterKey, componentJson.get(counterKey));
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'CounterData');
        }

        return retDataJson;
    }
}
